/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {FunctionComponent, MouseEventHandler} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {IconButton, IconButtonLabelPlacement, IconButtonProps} from '@ion/dls/src/components/buttons/IconButton';
import {
  SaveIconTooltip
} from '../../icons/SaveIcon';
import {
  SaveIconDefault
} from '../../icons/SaveIconDefault';
import {
  SaveIconInChange
} from '../../icons/SaveIconInChange';
import {
  SaveIconInProgress
} from '../../icons/SaveIconInProgress';
import {dlsColor} from '../../theme/theme';
import useThemeContext from '../../theme/useThemeContext';
import {IconProps} from '../../types/types';

export enum SaveIconTypes {
  DEFAULT = 'default',
  IN_PROGRESS = 'inprogress',
  CHANGED = 'changed'
}
export interface SaveIconButtonProps {
  onClick: MouseEventHandler<HTMLButtonElement>;
  type?: SaveIconTypes;
  buttonProps?: Omit<IconButtonProps, 'icon'>;
  iconProps?: IconProps;
  showCurrentLabel?: boolean;
  currentLabelPlacement?: IconButtonLabelPlacement;
  disableTooltip?: boolean;
};

// Provides the mapping between state prop and the respective save icon.
const saveIconMap = () => ({
  [SaveIconTypes.DEFAULT]     : SaveIconDefault,
  [SaveIconTypes.IN_PROGRESS] : SaveIconInProgress,
  [SaveIconTypes.CHANGED]     : SaveIconInChange
});

/**
 * Re-usable & Flexible Save Icon Button
 *
 * ```typescript
 *
 *  import {SaveIconButton} from '@ion/dls/src/components/buttons/';
 *
 *  <SaveIconButton type={'inprogress'}
 *    onClick={onClick} />
 *
 * ```
 */
export const SaveIconButton = ({
  onClick,
  buttonProps,
  iconProps,
  showCurrentLabel = true,
  currentLabelPlacement = IconButtonLabelPlacement.STACKED,
  disableTooltip = false,
  type = SaveIconTypes.DEFAULT
}: SaveIconButtonProps) => {
  const Icon: FunctionComponent<IconProps> = saveIconMap()[type];
  const {isCurrent: isCurrentTheme} = useThemeContext();
  const label                       = translate('common:editors.shared.save');
  const iconColor                   = type === SaveIconTypes.IN_PROGRESS ? dlsColor('black') : dlsColor('grey1');
  const button                      = (
    <IconButton shouldWrap={!isCurrentTheme}
      onClick={onClick}
      icon={<Icon color={iconColor} {...iconProps} />}
      color={iconProps?.color || iconColor}
      label={isCurrentTheme ? label : undefined}
      labelPlacement={currentLabelPlacement}
      hideLabel={isCurrentTheme && !showCurrentLabel}
      {...buttonProps} />
  );

  if (isCurrentTheme || disableTooltip) {
    return button;
  }

  return (
    <SaveIconTooltip title={label}>
      <div>
        {button}
      </div>
    </SaveIconTooltip>
  );
};
